// ─── Shared styles — earthy orange palette (matches cycloidal_drives.jsx) ──
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i)=><div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th,width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i)=>(
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─── Geometry helpers ────────────────────────────────────────────────────────
function ratchetTeethPath(cx, cy, r, teethCount, toothDepth, toothAngleFrac=0.55) {
  // toothAngleFrac: fraction of tooth pitch that is the "driving face" (near-radial)
  const pts = [];
  for (let i = 0; i < teethCount; i++) {
    const a0 = (2 * Math.PI * i) / teethCount - Math.PI / 2;
    const a1 = (2 * Math.PI * (i + toothAngleFrac)) / teethCount - Math.PI / 2;
    const a2 = (2 * Math.PI * (i + 1)) / teethCount - Math.PI / 2;
    const rInner = r - toothDepth;
    pts.push(`${(cx + r * Math.cos(a0)).toFixed(2)},${(cy + r * Math.sin(a0)).toFixed(2)}`);
    pts.push(`${(cx + rInner * Math.cos(a1)).toFixed(2)},${(cy + rInner * Math.sin(a1)).toFixed(2)}`);
    pts.push(`${(cx + r * Math.cos(a2)).toFixed(2)},${(cy + r * Math.sin(a2)).toFixed(2)}`);
  }
  return 'M ' + pts.join(' L ') + ' Z';
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Anatomy of a ratchet & pawl system
// Layout: wheel at cx=270 (left-centre), callouts clear of wheel, right panel x=580
// ─────────────────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W = 980, H = 450;
  const cx = 265, cy = 210;
  const R = 100, teethCount = 16, toothDepth = 13;

  // Pawl pivot: above and right of wheel, well inside the gap before the right panel
  const pawlPivotX = cx + R + 48, pawlPivotY = cy - 52;
  // Pawl tip engages tooth at ~1 o'clock position
  const tipAngle = -Math.PI / 2 + (2 * Math.PI * 1.1) / teethCount;
  const tipX = cx + (R - 1) * Math.cos(tipAngle);
  const tipY = cy + (R - 1) * Math.sin(tipAngle);
  const pawlLen = Math.sqrt((tipX - pawlPivotX) ** 2 + (tipY - pawlPivotY) ** 2);
  const pawlAngle = Math.atan2(tipY - pawlPivotY, tipX - pawlPivotX);
  const pw = 13;

  function pc(along, perp) {
    return [
      pawlPivotX + along * Math.cos(pawlAngle) - perp * Math.sin(pawlAngle),
      pawlPivotY + along * Math.sin(pawlAngle) + perp * Math.cos(pawlAngle),
    ];
  }
  const [p1x,p1y] = pc(0, -pw/2);
  const [p2x,p2y] = pc(0,  pw/2);
  const [p3x,p3y] = pc(pawlLen+5,  pw/2);
  const [p4x,p4y] = pc(pawlLen+5, -pw/2);

  // Spring goes upward from pivot to a frame bar above
  const springStartX = pawlPivotX, springStartY = pawlPivotY - 10;
  const springEndX   = pawlPivotX, springEndY   = pawlPivotY - 70;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Ratchet &amp; Pawl — Full Anatomy: Ratchet Wheel · Pawl · Pivot · Spring · Frame</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arRP" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <radialGradient id="rGrad" cx="50%" cy="50%" r="50%">
            <stop offset="60%" stopColor="#fdf4ec"/>
            <stop offset="100%" stopColor="#e8b07a" stopOpacity="0.5"/>
          </radialGradient>
        </defs>

        {/* ── Hub / wheel disc ── */}
        <circle cx={cx} cy={cy} r={R+4} fill="url(#rGrad)" stroke="#7a2e00" strokeWidth="2.5"/>
        <circle cx={cx} cy={cy} r={R-toothDepth-8} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={cx} cy={cy} r={15} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={cx} y={cy+4} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#fdf4ec">SHAFT</text>

        {/* ── Ratchet teeth ── */}
        <path d={ratchetTeethPath(cx, cy, R, teethCount, toothDepth)}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.85"/>

        {/* ── Permitted direction arc arrow (CW, outside bottom-right) ── */}
        <path d={`M ${cx+R+20} ${cy+22} A ${R+20} ${R+20} 0 0 0 ${cx+22} ${cy-R-20}`}
          fill="none" stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arRP)"/>
        <text x={cx+R+36} y={cy+38} fontSize="9" fontWeight="700" fill="#c45a10">ω (permitted)</text>

        {/* ── Pawl body ── */}
        <polygon points={`${p1x},${p1y} ${p2x},${p2y} ${p3x},${p3y} ${p4x},${p4y}`}
          fill="#a04010" stroke="#7a2e00" strokeWidth="2"/>
        {/* Pawl tip wedge */}
        <polygon points={`${tipX},${tipY} ${p3x},${p3y} ${p4x},${p4y}`}
          fill="#7a2e00" stroke="#7a2e00" strokeWidth="1"/>

        {/* ── Pawl pivot ── */}
        <circle cx={pawlPivotX} cy={pawlPivotY} r={8} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={pawlPivotX} cy={pawlPivotY} r={3.5} fill="#7a2e00"/>

        {/* ── Spring: vertical zigzag from pivot upward to frame bar ── */}
        {(() => {
          const segs = 7;
          const totalH = springEndY - springStartY; // negative (going up)
          const segH = totalH / segs;
          const amp = 8;
          let d = `M ${springStartX} ${springStartY}`;
          for (let i = 0; i < segs; i++) {
            const midX = springStartX + (i%2===0 ? amp : -amp);
            const midY = springStartY + segH * (i + 0.5);
            const endY = springStartY + segH * (i + 1);
            d += ` Q ${midX} ${midY} ${springStartX} ${endY}`;
          }
          return <path d={d} fill="none" stroke="#7a2e00" strokeWidth="2.2"/>;
        })()}

        {/* ── Frame bar (horizontal, above spring) ── */}
        <rect x={pawlPivotX-30} y={springEndY-12} width={60} height={12} rx={2}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="1.8"/>
        {/* Cross-hatch marks indicating fixed frame */}
        {[-18,-10,-2,6,14].map((ox,i)=>(
          <line key={i}
            x1={pawlPivotX+ox}   y1={springEndY-12}
            x2={pawlPivotX+ox-7} y2={springEndY-21}
            stroke="#7a2e00" strokeWidth="1.2"/>
        ))}

        {/* Frame label — to the right of the frame bar */}
        <line x1={pawlPivotX+30} y1={springEndY-6} x2={pawlPivotX+50} y2={springEndY-6} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={pawlPivotX+50} y={springEndY-20} width={90} height={28} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={pawlPivotX+95} y={springEndY-6} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Frame / Housing</text>
        <text x={pawlPivotX+95} y={springEndY+6} textAnchor="middle" fontSize="8" fill="#a04010">fixed structure</text>

        {/* Return spring label — to the right of the spring midpoint */}
        <line x1={pawlPivotX+10} y1={(springStartY+springEndY)/2}
              x2={pawlPivotX+52} y2={(springStartY+springEndY)/2} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={pawlPivotX+52} y={(springStartY+springEndY)/2-20} width={110} height={28} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={pawlPivotX+107} y={(springStartY+springEndY)/2-6} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Return Spring</text>
        <text x={pawlPivotX+107} y={(springStartY+springEndY)/2+6} textAnchor="middle" fontSize="8" fill="#a04010">leaf or coil type</text>

        {/* ── Callout: Ratchet Wheel — straight down from bottom of wheel ── */}
        <line x1={cx} y1={cy+R+4} x2={cx} y2={cy+R+34} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={cx-86} y={cy+R+34} width={172} height={32} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx} y={cy+R+50} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Ratchet Wheel</text>
        <text x={cx} y={cy+R+62} textAnchor="middle" fontSize="8.5" fill="#a04010">{teethCount} asymmetric teeth · hardened steel</text>

        {/* ── Callout: Pawl — leader from centre of pawl body, going left and down ── */}
        {(() => {
          const midX = (p1x+p3x)/2, midY = (p1y+p3y)/2;
          const lx = midX - 50, ly = midY + 50;
          return <g>
            <line x1={midX} y1={midY} x2={lx} y2={ly} stroke="#c45a10" strokeWidth="1.2"/>
            <rect x={lx-100} y={ly-2} width={100} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
            <text x={lx-50} y={ly+14} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Pawl</text>
            <text x={lx-50} y={ly+26} textAnchor="middle" fontSize="8.5" fill="#a04010">hardened tip · pivoting</text>
          </g>;
        })()}

        {/* ── Callout: Asymmetric Tooth — upper-left of wheel ── */}
        {(() => {
          const ta = -Math.PI/2 - (2*Math.PI*2.5)/teethCount;
          const lx = cx + (R+12)*Math.cos(ta), ly = cy + (R+12)*Math.sin(ta);
          return <g>
            <line x1={lx} y1={ly} x2={lx-28} y2={ly-22} stroke="#c45a10" strokeWidth="1.2"/>
            <rect x={lx-148} y={ly-52} width={118} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
            <text x={lx-89} y={ly-34} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Asymmetric Tooth</text>
            <text x={lx-89} y={ly-22} textAnchor="middle" fontSize="8" fill="#a04010">steep face · gradual back</text>
          </g>;
        })()}

        {/* ── Right info panel: x=580, width=390 → right edge 970 < W=980 ── */}
        <rect x={578} y={12} width={390} height={420} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={773} y={32} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Key Operating Principles</text>
        <line x1={586} y1={39} x2={960} y2={39} stroke="#e8b07a"/>
        {[
          ['Function:',      'Transmit rotation one direction only'],
          ['',               'Block rotation in reverse direction'],
          ['',               'Convert oscillating → intermittent'],
          ['',               ''],
          ['Components:',    'Ratchet wheel (toothed)'],
          ['',               'Driving or holding pawl'],
          ['',               'Pivot pin (pawl)'],
          ['',               'Return spring'],
          ['',               'Frame / housing'],
          ['',               ''],
          ['Tooth geometry:','Driving face: nearly radial (5–15°)'],
          ['',               'Back face: gradual slope (20–45°)'],
          ['',               'Asymmetry enables one-way action'],
          ['',               ''],
          ['Pawl action:',   'Forward: tip rides over back face'],
          ['',               'Reverse: face locks on steep face'],
          ['',               ''],
          ['Contact force:', 'Tangential → radial load on pivot'],
          ['',               'Spring pre-load keeps engagement'],
          ['',               ''],
          ['Materials:',     'Wheel: carbon steel, case-hardened'],
          ['',               'Pawl: alloy steel, hardened tip'],
          ['',               'Spring: spring steel / stainless'],
          ['',               ''],
          ['Efficiency:',    '~98–99.5%  (driving direction)'],
          ['Locking:',       'Positive — no slippage possible'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={588} y={53+i*14.5} fontSize="9" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={l?700:588} y={53+i*14.5} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The asymmetric tooth profile is the fundamental feature: the near-radial driving face locks instantly under reverse load, while the sloped back allows the pawl to ride over during forward rotation</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Operating modes: 4 configurations
// ─────────────────────────────────────────────────────────────────────────────
function DiagramModes() {
  const W = 920, H = 360;
  const configs = [
    { label:'Holding Pawl', note:'Wheel free to turn CW;\nblocks CCW reversal', cx:110, dir:1, pawlSide:1 },
    { label:'Driving Pawl', note:'Lever input drives CW;\nback-swing is free', cx:330, dir:1, pawlSide:-1 },
    { label:'Double Pawl\n(Anti-backlash)', note:'Two pawls offset ½ pitch;\nminimises dead angle', cx:550, dir:1, pawlSide:1, dual:true },
    { label:'Reversible Ratchet', note:'Flip pawl to choose\ndirection of locking', cx:770, dir:-1, pawlSide:-1 },
  ];
  const R = 62, teeth = 12, td = 10;

  function MiniRatchet({cx, cy, dir, pawlSide, dual}) {
    const tipA = -Math.PI/2 + (2*Math.PI*1.05)/teeth * dir;
    const tipX = cx + (R-1)*Math.cos(tipA), tipY = cy + (R-1)*Math.sin(tipA);
    const ppx = cx + (R+34)*Math.cos(tipA - 0.7*dir);
    const ppy = cy + (R+34)*Math.sin(tipA - 0.7*dir);
    const pAngle = Math.atan2(tipY - ppy, tipX - ppx);
    const pw = 10;
    function pc(al, pr) {
      return [ppx + al*Math.cos(pAngle) - pr*Math.sin(pAngle), ppy + al*Math.sin(pAngle) + pr*Math.cos(pAngle)];
    }
    const pLen = Math.sqrt((tipX-ppx)**2+(tipY-ppy)**2);
    const [a1x,a1y]=pc(0,-pw/2);
    const [a2x,a2y]=pc(0,pw/2);
    const [a3x,a3y]=pc(pLen+4,pw/2);
    const [a4x,a4y]=pc(pLen+4,-pw/2);

    // second pawl for dual mode: offset by half pitch
    const tipA2 = tipA + Math.PI/teeth;
    const tipX2 = cx + (R-1)*Math.cos(tipA2), tipY2 = cy + (R-1)*Math.sin(tipA2);
    const ppx2 = cx + (R+30)*Math.cos(tipA2-0.7);
    const ppy2 = cy + (R+30)*Math.sin(tipA2-0.7);
    const pAngle2 = Math.atan2(tipY2-ppy2, tipX2-ppx2);
    const pLen2 = Math.sqrt((tipX2-ppx2)**2+(tipY2-ppy2)**2);
    function pc2(al,pr) {
      return [ppx2+al*Math.cos(pAngle2)-pr*Math.sin(pAngle2), ppy2+al*Math.sin(pAngle2)+pr*Math.cos(pAngle2)];
    }
    const [b1x,b1y]=pc2(0,-pw/2);
    const [b2x,b2y]=pc2(0,pw/2);
    const [b3x,b3y]=pc2(pLen2+4,pw/2);
    const [b4x,b4y]=pc2(pLen2+4,-pw/2);

    return (
      <g>
        <circle cx={cx} cy={cy} r={R+2} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.8"/>
        <circle cx={cx} cy={cy} r={R-td-6} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
        <circle cx={cx} cy={cy} r={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        <path d={ratchetTeethPath(cx, cy, R, teeth, td, dir > 0 ? 0.55 : 0.45)}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
        {/* direction arrow */}
        <path d={dir>0
          ? `M ${cx+R+16} ${cy+12} A ${R+16} ${R+16} 0 0 0 ${cx+12} ${cy-R-16}`
          : `M ${cx+12} ${cy-R-16} A ${R+16} ${R+16} 0 0 1 ${cx+R+16} ${cy+12}`}
          fill="none" stroke="#c45a10" strokeWidth="2" markerEnd="url(#arMD)"/>
        {/* primary pawl */}
        <polygon points={`${a1x},${a1y} ${a2x},${a2y} ${a3x},${a3y} ${a4x},${a4y}`}
          fill="#a04010" stroke="#7a2e00" strokeWidth="1.5"/>
        <polygon points={`${tipX},${tipY} ${a3x},${a3y} ${a4x},${a4y}`}
          fill="#7a2e00" stroke="#7a2e00" strokeWidth="1"/>
        <circle cx={ppx} cy={ppy} r={6} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
        {/* second pawl for dual */}
        {dual && <>
          <polygon points={`${b1x},${b1y} ${b2x},${b2y} ${b3x},${b3y} ${b4x},${b4y}`}
            fill="#7a4a28" stroke="#7a2e00" strokeWidth="1.5" opacity="0.8"/>
          <polygon points={`${tipX2},${tipY2} ${b3x},${b3y} ${b4x},${b4y}`}
            fill="#7a2e00" stroke="#7a2e00" strokeWidth="1"/>
          <circle cx={ppx2} cy={ppy2} r={6} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
        </>}
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Four Fundamental Operating Configurations</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arMD" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>
        {configs.map((cfg,i) => {
          const cy = 155;
          return (
            <g key={i}>
              <MiniRatchet cx={cfg.cx} cy={cy} dir={cfg.dir} pawlSide={cfg.pawlSide} dual={cfg.dual}/>
              <rect x={cfg.cx-74} y={cy+R+20} width={148} height={42} rx={4}
                fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
              <text x={cfg.cx} y={cy+R+36} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">
                {cfg.label.split('\n')[0]}
              </text>
              {cfg.label.split('\n')[1] && (
                <text x={cfg.cx} y={cy+R+48} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">
                  {cfg.label.split('\n')[1]}
                </text>
              )}
              {cfg.note.split('\n').map((line,j)=>(
                <text key={j} x={cfg.cx} y={cy+R+60+(j+(!cfg.label.includes('\n')?1:0))*13}
                  textAnchor="middle" fontSize="8.5" fill="#a04010">{line}</text>
              ))}
            </g>
          );
        })}
        {/* Dividers */}
        {[220,440,660].map((x,i)=>(
          <line key={i} x1={x} y1={20} x2={x} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
        ))}
        {/* Summary bar */}
        <rect x={10} y={H-28} width={W-20} height={20} rx={4} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={W/2} y={H-14} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">
          Same wheel and pawl — configuration determines function: holding vs. driving vs. no-backlash vs. reversible
        </text>
      </svg>
      <div style={S.dCaption}>The double-pawl arrangement (third) offsets two pawls by half a tooth pitch, halving the maximum slack angle; widely used in hand tools, winches, and bicycle freewheels</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Tooth geometry, self-locking condition, force analysis
//
// LAYOUT (all values hard-coded, zones never overlap):
//
//  x:  0        160      260      380      500                    980
//      |  LEFT   | TOOTH  | RIGHT  | DEPTH  | RIGHT PANEL          |
//      | callouts|        | callout| arrow  |                      |
//
//  Tooth: rootL=200 rootR=340  tipL=208 tipR=296
//         rootY=280  tipY=110  → height 170px
//
//  Zone allocation:
//    x < 155   → F_N arrowhead + label (fires LEFT from driving face)
//    x 155–198 → leader from face to F_N box
//    x 200–340 → tooth body only
//    x 200–208 → driving face (left edge) — α arc hangs left of x=200
//    x 296–340 → back face (right edge) — β arc hangs right of x=296,
//                but β label goes BELOW the back face tip (y > tipY+50)
//    x 350–490 → tooth-depth arrow + "Back/Cam face" callout (y > 200)
//    x 500+    → right panel
//
//  F_t fires RIGHTWARD from tooth back-face mid-point into x 350–490 zone
//  Radial line is at x=130 (left of tooth), going upward — no conflict
//  α arc centred at TL (x=208,y=110), swings LEFT into x<200 zone
//  α label at x≈165, y≈170  (below arc, left of tooth)
//  β arc centred at TR (x=296,y=110), swings RIGHT into x>296 zone
//  β label at x≈340, y≈165  (right of tooth tip, above back callout)
// ─────────────────────────────────────────────────────────────────────────────
function DiagramToothGeometry() {
  const W = 980, H = 390;

  // ── Tooth vertices ────────────────────────────────────────────────────────
  const rootY = 280, tipY = 110;
  const rootL = 200, rootR = 340;
  const tipL  = 208, tipR  = 296;   // driving face barely leans; back face slopes well

  // ── Ghost tooth to the left (context) ────────────────────────────────────
  const gW = rootR - rootL;   // = 140
  const gRootL = rootL - gW - 8, gRootR = rootL - 2;
  const gTipL  = gRootL + (tipL - rootL);
  const gTipR  = gRootR + (tipR - rootR);   // negative lean on ghost

  // ── Radial reference line ─────────────────────────────────────────────────
  // Placed at x=130, well left of tooth — unambiguously separate
  const radX = 130;

  // ── Angle arcs ────────────────────────────────────────────────────────────
  // α arc: centred at TL=(tipL,tipY). Driving face goes TL→RL=(rootL,rootY).
  // Angle of driving face from +x axis:
  const dfVx = rootL - tipL, dfVy = rootY - tipY;   // (−8, +170) → ≈ −177° → actually atan2(170,−8) ≈ 92° below +x
  const dfAng = Math.atan2(dfVy, dfVx);              // ≈ 1.618 rad (≈93°, just past straight down)
  // Arc from straight-down (π/2) to dfAng, sweeping CCW (large sweep because face nearly vertical)
  // Since dfAng ≈ 93° and π/2 = 90°, the arc is tiny → that's correct: α is ~3° (very steep face)
  // For visibility, draw the arc at a larger radius and sweep from a nominal 10° past vertical:
  const arcRa = 36;
  const aStart = Math.PI/2 + 0.18;   // 10° past straight-down (on the left side)
  const aEnd   = dfAng;
  const ax1 = tipL + arcRa*Math.cos(aStart), ay1 = tipY + arcRa*Math.sin(aStart);
  const ax2 = tipL + arcRa*Math.cos(aEnd),   ay2 = tipY + arcRa*Math.sin(aEnd);
  // α label: to the LEFT of TL, at the arc midpoint height
  const aLabelX = tipL - 58, aLabelY = tipY + arcRa + 10;

  // β arc: centred at TR=(tipR,tipY). Back face goes TR→RR=(rootR,rootY).
  const bfVx = rootR - tipR, bfVy = rootY - tipY;   // (+44, +170)
  const bfAng = Math.atan2(bfVy, bfVx);             // ≈ 75°
  const arcRb = 38;
  const bx1 = tipR + arcRb*Math.cos(Math.PI/2), by1 = tipY + arcRb*Math.sin(Math.PI/2);
  const bx2 = tipR + arcRb*Math.cos(bfAng),     by2 = tipY + arcRb*Math.sin(bfAng);
  // β label: to the RIGHT of TR, at arc midpoint height
  const bMid = (Math.PI/2 + bfAng)/2;
  const bLabelX = tipR + arcRb + 22;
  const bLabelY = tipY + arcRb*0.7;

  // ── Force vectors ─────────────────────────────────────────────────────────
  // F_N: fires LEFT from mid-driving-face. Origin well above α arc zone.
  const fnOx = tipL + (rootL-tipL)*0.20;  // ≈ x=206 (near top of driving face)
  const fnOy = tipY + (rootY-tipY)*0.20;  // ≈ y=144
  // Left-normal of driving face:
  const dfLen = Math.sqrt(dfVx*dfVx+dfVy*dfVy);
  const fnNx = -dfVy/dfLen, fnNy = dfVx/dfLen;  // points left+slightly up
  const fnLen = 82;
  const fnEx = fnOx + fnNx*fnLen;   // ≈ x=124 — well left of tooth, right of radial line
  const fnEy = fnOy + fnNy*fnLen;   // ≈ y=140

  // F_t: fires RIGHTWARD from mid-back-face. Origin at y≈190 (below β arc zone at y≈148).
  const ftOx = tipR + (rootR-tipR)*0.35;  // ≈ x=311
  const ftOy = tipY + (rootY-tipY)*0.35;  // ≈ y=170
  const ftLen = 78;
  const ftEx = ftOx + ftLen;   // ≈ x=389 — to the right of tooth, left of right panel
  const ftEy = ftOy;           // horizontal

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Tooth Geometry — Driving &amp; Back Faces, Self-Locking Condition, Force Analysis</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arTG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arTGb" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#c45a10"/>
          </marker>
          <marker id="arFN" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0,7 4,0 8" fill="#7a2e00"/>
          </marker>
          <marker id="arFT" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0,7 4,0 8" fill="#c45a10"/>
          </marker>
        </defs>

        {/* ── Rim bar ── */}
        <rect x={gRootL - 4} y={rootY} width={rootR - gRootL + 60} height={26}
          fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.8" rx={3}/>

        {/* ── Ghost tooth (left context) ── */}
        <polygon points={`${gRootL},${rootY} ${gRootR},${rootY} ${gTipR},${tipY+52} ${gTipL},${tipY+52}`}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="1.4" opacity="0.4"/>

        {/* ── Main tooth ── */}
        <polygon points={`${rootL},${rootY} ${rootR},${rootY} ${tipR},${tipY} ${tipL},${tipY}`}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5" opacity="0.92"/>

        {/* ── Driving face highlight (dark thick stroke on left edge) ── */}
        <line x1={tipL} y1={tipY} x2={rootL} y2={rootY}
          stroke="#5a1e00" strokeWidth="5" strokeLinecap="round"/>

        {/* ── Back face highlight (medium-dark thick stroke on right edge) ── */}
        <line x1={tipR} y1={tipY} x2={rootR} y2={rootY}
          stroke="#a04010" strokeWidth="5" strokeLinecap="round"/>

        {/* ── Radial reference line (dashed, at x=radX=130, well left of tooth) ── */}
        <line x1={radX} y1={rootY+10} x2={radX} y2={tipY-45}
          stroke="#b08040" strokeWidth="1.8" strokeDasharray="7,4"/>
        <text x={radX} y={tipY-54} textAnchor="middle" fontSize="8.5" fontStyle="italic" fill="#a04010">radial</text>
        <text x={radX} y={tipY-42} textAnchor="middle" fontSize="8.5" fontStyle="italic" fill="#a04010">direction</text>

        {/* ── α arc (left of TL, tiny wedge showing driving face nearness to radial) ── */}
        <path d={`M ${ax1} ${ay1} A ${arcRa} ${arcRa} 0 0 0 ${ax2} ${ay2}`}
          fill="none" stroke="#5a1e00" strokeWidth="2.2"/>
        {/* α label — to the left of tooth tip, below arc */}
        <text x={aLabelX + 14} y={aLabelY - 4} textAnchor="middle" fontSize="15" fontWeight="700" fill="#5a1e00">α</text>
        <text x={aLabelX + 14} y={aLabelY + 12} textAnchor="middle" fontSize="8.5" fill="#a04010">5°–15°</text>

        {/* ── β arc (right of TR) ── */}
        <path d={`M ${bx1} ${by1} A ${arcRb} ${arcRb} 0 0 1 ${bx2} ${by2}`}
          fill="none" stroke="#a04010" strokeWidth="2.2"/>
        <text x={bLabelX} y={bLabelY} fontSize="15" fontWeight="700" fill="#a04010">β</text>
        <text x={bLabelX} y={bLabelY + 16} fontSize="8.5" fill="#a04010">20°–45°</text>

        {/* ── F_N vector (fires LEFT from upper driving face) ── */}
        <circle cx={fnOx} cy={fnOy} r={4} fill="#5a1e00"/>
        <line x1={fnOx} y1={fnOy} x2={fnEx} y2={fnEy}
          stroke="#5a1e00" strokeWidth="3" markerEnd="url(#arFN)"/>
        {/* F_N label box — anchored at far left, well clear of arc */}
        <rect x={4} y={fnEy - 28} width={112} height={44} rx={4}
          fill="#fdf0e4" stroke="#5a1e00" strokeWidth="1.4"/>
        <text x={60} y={fnEy - 12} textAnchor="middle" fontSize="10" fontWeight="700" fill="#5a1e00">F_N</text>
        <text x={60} y={fnEy + 2}  textAnchor="middle" fontSize="8"   fill="#a04010">normal force</text>
        <text x={60} y={fnEy + 14} textAnchor="middle" fontSize="7.5" fill="#7a4a28">(⊥ driving face)</text>

        {/* ── F_t vector (fires RIGHT from mid-back-face, horizontal) ── */}
        <circle cx={ftOx} cy={ftOy} r={4} fill="#c45a10"/>
        <line x1={ftOx} y1={ftOy} x2={ftEx} y2={ftEy}
          stroke="#c45a10" strokeWidth="2.5" strokeDasharray="7,3" markerEnd="url(#arFT)"/>
        {/* F_t label — to the right of arrowhead */}
        <text x={ftEx + 6} y={ftEy - 5}  fontSize="10" fontWeight="700" fill="#c45a10">F_t</text>
        <text x={ftEx + 6} y={ftEy + 8}  fontSize="8.5" fill="#a04010">tangential</text>
        <text x={ftEx + 6} y={ftEy + 20} fontSize="7.5" fill="#7a4a28">(drives output)</text>

        {/* ── "Driving / Locking Face" callout box — bottom-left ── */}
        <line x1={tipL + (rootL-tipL)*0.55} y1={tipY + (rootY-tipY)*0.55}
              x2={10} y2={rootY - 30}
          stroke="#5a1e00" strokeWidth="1.3"/>
        <rect x={4} y={rootY - 26} width={158} height={30} rx={4}
          fill="#fdf0e4" stroke="#5a1e00" strokeWidth="1.5"/>
        <text x={83} y={rootY - 10} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#5a1e00">Driving / Locking Face</text>
        <text x={83} y={rootY + 2}  textAnchor="middle" fontSize="8"   fill="#a04010">near-radial · self-locking</text>

        {/* ── "Back / Cam Face" callout box — below-right of back face ── */}
        <line x1={tipR + (rootR-tipR)*0.55} y1={tipY + (rootY-tipY)*0.55}
              x2={358} y2={rootY - 30}
          stroke="#a04010" strokeWidth="1.3"/>
        <rect x={358} y={rootY - 26} width={136} height={30} rx={4}
          fill="#fdf0e4" stroke="#a04010" strokeWidth="1.5"/>
        <text x={426} y={rootY - 10} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#a04010">Back / Cam Face</text>
        <text x={426} y={rootY + 2}  textAnchor="middle" fontSize="8"   fill="#a04010">sloped · pawl rides over</text>

        {/* ── Tooth pitch arrow (below rim, left side) ── */}
        <line x1={gRootL} y1={rootY + 48} x2={rootL} y2={rootY + 48}
          stroke="#c45a10" strokeWidth="1.5" markerStart="url(#arTGb)" markerEnd="url(#arTG)"/>
        <text x={(gRootL + rootL)/2} y={rootY + 63} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10">p  (tooth pitch)</text>
        <text x={(gRootL + rootL)/2} y={rootY + 76} textAnchor="middle" fontSize="8"   fill="#a04010">p = π · D / z</text>

        {/* ── Tooth depth arrow (right of main tooth, clear of back callout) ── */}
        <line x1={rootR + 18} y1={rootY} x2={rootR + 18} y2={tipY}
          stroke="#c45a10" strokeWidth="1.5" markerStart="url(#arTGb)" markerEnd="url(#arTG)"/>
        <text x={rootR + 32} y={(rootY+tipY)/2 - 4} fontSize="8.5" fontWeight="700" fill="#c45a10">h</text>
        <text x={rootR + 32} y={(rootY+tipY)/2 + 9} fontSize="7.5" fill="#7a4a28">tooth</text>
        <text x={rootR + 32} y={(rootY+tipY)/2 + 21} fontSize="7.5" fill="#7a4a28">depth</text>

        {/* ── Right equations panel (x=500, width=472) ── */}
        <rect x={500} y={10} width={472} height={368} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={736} y={30} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Tooth Design Equations &amp; Self-Locking</text>
        <line x1={508} y1={37} x2={964} y2={37} stroke="#e8b07a"/>
        {[
          ['Tooth pitch:',          'p = π · D / z',                            true ],
          ['',                      'D = wheel diameter,  z = tooth count',      false],
          ['',                      '',                                           false],
          ['Driving face α:',       '5° – 15° from radial line',                 true ],
          ['',                      '→ near-radial: near-zero ejection force',    false],
          ['',                      '→ self-locking when α < friction angle φ',  false],
          ['',                      '',                                           false],
          ['Back face β:',          '20° – 45° from radial',                     true ],
          ['',                      '→ pawl rides over during forward stroke',    false],
          ['',                      '→ larger β = lower spring pre-load needed',  false],
          ['',                      '',                                           false],
          ['Self-locking:',         'α < φ   (φ = arctan μ)',                     true ],
          ['',                      'μ ≈ 0.1–0.2  (steel on steel, lubricated)', false],
          ['',                      'φ ≈ 6°–11°',                               false],
          ['',                      'If α < φ: pawl cannot be ejected',          false],
          ['',                      '→ positive lock regardless of load',         false],
          ['',                      '',                                           false],
          ['Back-face lift force:', 'F_lift = F_N · tan(β)',                      true ],
          ['',                      'Spring pre-load must supply ≥ F_lift',       false],
          ['',                      '',                                           false],
          ['Normal force F_N:',     'F_N = T_lock / (R · cos α)',                 true ],
          ['',                      'T_lock = locking torque,  R = pitch radius', false],
          ['',                      '',                                           false],
          ['Contact stress:',       'σ_H = 0.418 √(F_N · E* / (r_tip · L))',     true ],
          ['',                      'r_tip = pawl tip radius, L = face width',    false],
          ['',                      'E* = combined elastic modulus',               false],
        ].map(([l,v,hdr],i)=>{
          const y = 52 + i*12.4;
          const mono = !l && v && v.match(/[=<≥·√→]/);
          return (
            <g key={i}>
              {l && <text x={510} y={y} fontSize="9" fontWeight="700" fill="#7a2e00">{l}</text>}
              <text x={l ? 660 : 518} y={y} fontSize="9"
                fontFamily={mono ? "'Courier New',monospace" : undefined}
                fill={hdr && l ? '#c45a10' : '#a04010'}>{v}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>The self-locking condition (α &lt; φ) is the critical design requirement: if the driving face angle is shallower than the friction angle, the ratchet is geometrically impossible to back-drive regardless of applied torque magnitude</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Mechanisms derived from ratchet & pawl (bar chart of max angle)
// ─────────────────────────────────────────────────────────────────────────────
function DiagramDeadAngle() {
  const W = 900, H = 290;
  const data = [
    { label: 'z = 6',  teeth: 6,  angle: 60 },
    { label: 'z = 8',  teeth: 8,  angle: 45 },
    { label: 'z = 12', teeth: 12, angle: 30 },
    { label: 'z = 16', teeth: 16, angle: 22.5 },
    { label: 'z = 24', teeth: 24, angle: 15 },
    { label: 'z = 36', teeth: 36, angle: 10 },
    { label: 'z = 48', teeth: 48, angle: 7.5 },
    { label: 'Dual z=12', teeth: 24, angle: 15, dual:true },
    { label: 'Dual z=24', teeth: 48, angle: 7.5, dual:true },
  ];
  const barBase = 220, maxH = 150, maxAngle = 60;
  const barW = 62, gap = 10, startX = 54;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Dead Angle (Maximum Reverse Free-Play) vs. Tooth Count  — single vs. double pawl</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {/* Axes */}
        <line x1={44} y1={barBase-maxH-10} x2={44} y2={barBase+4} stroke="#7a2e00" strokeWidth="1.8"/>
        <line x1={44} y1={barBase} x2={W-20} y2={barBase} stroke="#7a2e00" strokeWidth="1.8"/>
        {/* Y axis labels */}
        {[0,10,20,30,40,50,60].map(v=>{
          const y = barBase - (v/maxAngle)*maxH;
          return <g key={v}>
            <line x1={40} y1={y} x2={W-20} y2={y} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
            <text x={38} y={y+4} textAnchor="end" fontSize="9" fill="#7a4a28">{v}°</text>
          </g>;
        })}
        <text x={16} y={barBase-maxH/2} textAnchor="middle" fontSize="9" fontWeight="700"
          fill="#7a2e00" transform={`rotate(-90,16,${barBase-maxH/2})`}>Dead Angle (°)</text>

        {/* Bars */}
        {data.map((d,i)=>{
          const bh = (d.angle/maxAngle)*maxH;
          const bx = startX + i*(barW+gap);
          const by = barBase - bh;
          return <g key={i}>
            <rect x={bx} y={by} width={barW} height={bh} rx={3}
              fill={d.dual?'#7a2e00':'#c45a10'} stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
            <text x={bx+barW/2} y={by-5} textAnchor="middle" fontSize="9.5" fontWeight="700"
              fill={d.dual?'#7a2e00':'#c45a10'}>{d.angle}°</text>
            <text x={bx+barW/2} y={barBase+14} textAnchor="middle" fontSize="8.5" fill="#7a2e00">{d.label}</text>
            <text x={bx+barW/2} y={barBase+26} textAnchor="middle" fontSize="8"
              fill={d.dual?'#7a2e00':'#a04010'}>{d.dual?'dual':'single'}</text>
          </g>;
        })}

        {/* Legend */}
        <rect x={W-180} y={20} width={155} height={52} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <rect x={W-172} y={32} width={14} height={14} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1}"/>
        <text x={W-152} y={43} fontSize="9" fill="#7a2e00">Single pawl</text>
        <rect x={W-172} y={52} width={14} height={14} rx={2} fill="#7a2e00" stroke="#7a2e00" strokeWidth="1"/>
        <text x={W-152} y={63} fontSize="9" fill="#7a2e00">Dual pawl (½ pitch offset)</text>
      </svg>
      <div style={S.dCaption}>Dead angle = 360°/z for single pawl · 180°/z for dual pawl — this is the maximum reverse rotation before the pawl re-engages; critical for winch, lifting, and indexing applications</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Ratchet variants: linear rack, internal, silent ratchet freewheels
// ─────────────────────────────────────────────────────────────────────────────
function DiagramVariants() {
  const W = 960, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Ratchet Mechanism Variants — Rotary · Linear Rack · Internal Tooth · Roller-Clutch (Silent) Ratchet</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {/* ─── 1. Standard rotary (compact) ─── */}
        {(()=>{
          const cx=112, cy=148, R=72, teeth=10, td=11;
          return <g>
            <circle cx={cx} cy={cy} r={R+4} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={cx} cy={cy} r={R-td-8} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={cx} cy={cy} r={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
            <path d={ratchetTeethPath(cx,cy,R,teeth,td)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
            {/* Pawl */}
            {(()=>{
              const ta=-Math.PI/2+(2*Math.PI*1.05)/teeth;
              const tx=cx+(R-1)*Math.cos(ta), ty=cy+(R-1)*Math.sin(ta);
              const ppx=cx+(R+28)*Math.cos(ta-0.6), ppy=cy+(R+28)*Math.sin(ta-0.6);
              const pa=Math.atan2(ty-ppy,tx-ppx), pw=9, pl=Math.sqrt((tx-ppx)**2+(ty-ppy)**2);
              const c=(al,pr)=>[ppx+al*Math.cos(pa)-pr*Math.sin(pa),ppy+al*Math.sin(pa)+pr*Math.cos(pa)];
              const [a,b,d,e]=[c(0,-pw/2),c(0,pw/2),c(pl+4,pw/2),c(pl+4,-pw/2)];
              return <>
                <polygon points={`${a[0]},${a[1]} ${b[0]},${b[1]} ${d[0]},${d[1]} ${e[0]},${e[1]}`}
                  fill="#a04010" stroke="#7a2e00" strokeWidth="1.5"/>
                <circle cx={ppx} cy={ppy} r={5} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.2"/>
              </>;
            })()}
            <text x={cx} y={cy+R+22} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Standard Rotary</text>
            <text x={cx} y={cy+R+35} textAnchor="middle" fontSize="8.5" fill="#a04010">External teeth · pivoting pawl</text>
          </g>;
        })()}

        {/* ─── 2. Linear rack ratchet ─── */}
        {(()=>{
          const rx=240, ry=108, rw=190, rh=32, teeth=8, tw=rw/teeth, td=14;
          // Draw rack bar
          return <g>
            <rect x={rx} y={ry} width={rw} height={rh} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Teeth on top of rack */}
            {Array.from({length:teeth},(_,i)=>{
              const tx=rx+i*tw;
              return <polygon key={i}
                points={`${tx},${ry} ${tx+tw*0.6},${ry} ${tx+tw*0.6},${ry-td} ${tx+tw},${ry}`}
                fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>;
            })}
            {/* Pawl above rack */}
            <polygon points={`${rx+70},${ry-td-20} ${rx+90},${ry-td-20} ${rx+82},${ry-td+2}`}
              fill="#a04010" stroke="#7a2e00" strokeWidth="1.8"/>
            {/* Pivot */}
            <circle cx={rx+80} cy={ry-td-30} r={7} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Arrow showing permitted linear direction */}
            <path d={`M ${rx+rw+10} ${ry+rh/2}`} stroke="#c45a10" strokeWidth="2"/>
            <line x1={rx} y1={ry+rh+16} x2={rx+rw} y2={ry+rh+16}
              stroke="#c45a10" strokeWidth="2" markerEnd="url(#arVR)"/>
            <line x1={rx+rw} y1={ry+rh+16} x2={rx+rw-18} y2={ry+rh+8}
              stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="4,2"/>
            <text x={rx+8} y={ry+rh+30} fontSize="8.5" fill="#c45a10">permitted →</text>
            <text x={rx+rw-72} y={ry+rh+30} fontSize="8.5" fill="#7a2e00">blocked ←</text>
            <text x={rx+rw/2} y={ry+rh+52} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Linear Rack Ratchet</text>
            <text x={rx+rw/2} y={ry+rh+64} textAnchor="middle" fontSize="8.5" fill="#a04010">Translational one-way lock · tie-downs, jacks</text>
          </g>;
        })()}

        {/* ─── 3. Internal-tooth ratchet ─── */}
        {(()=>{
          const cx=620, cy=148, Ro=82, teeth=12, td=12;
          // External ring with internal teeth
          return <g>
            <circle cx={cx} cy={cy} r={Ro} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2.5"/>
            {/* Internal teeth (point inward) */}
            {Array.from({length:teeth},(_,i)=>{
              const a0=(2*Math.PI*i)/teeth - Math.PI/2;
              const a1=(2*Math.PI*(i+0.5))/teeth - Math.PI/2;
              const a2=(2*Math.PI*(i+1))/teeth - Math.PI/2;
              const Ri=Ro-td;
              return <polygon key={i}
                points={`${cx+Ro*Math.cos(a0)},${cy+Ro*Math.sin(a0)}
                  ${cx+Ri*Math.cos(a1)},${cy+Ri*Math.sin(a1)}
                  ${cx+Ro*Math.cos(a2)},${cy+Ro*Math.sin(a2)}
                  ${cx+(Ro-2)*Math.cos((a0+a2)/2)},${cy+(Ro-2)*Math.sin((a0+a2)/2)}`}
                fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>;
            })}
            {/* Inner wheel (smooth) */}
            <circle cx={cx} cy={cy} r={Ro-td-12} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.8"/>
            <circle cx={cx} cy={cy} r={12} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Pawl pointing outward from inner hub */}
            {(()=>{
              const ta = -Math.PI/2 + (2*Math.PI*0.8)/teeth;
              const tx = cx+(Ro-td-1)*Math.cos(ta), ty = cy+(Ro-td-1)*Math.sin(ta);
              const ppx=cx+(Ro-td-32)*Math.cos(ta+0.4), ppy=cy+(Ro-td-32)*Math.sin(ta+0.4);
              const pa=Math.atan2(ty-ppy,tx-ppx), pw=9, pl=Math.sqrt((tx-ppx)**2+(ty-ppy)**2);
              const c=(al,pr)=>[ppx+al*Math.cos(pa)-pr*Math.sin(pa),ppy+al*Math.sin(pa)+pr*Math.cos(pa)];
              const [a,b,d,e]=[c(0,-pw/2),c(0,pw/2),c(pl+4,pw/2),c(pl+4,-pw/2)];
              return <>
                <polygon points={`${a[0]},${a[1]} ${b[0]},${b[1]} ${d[0]},${d[1]} ${e[0]},${e[1]}`}
                  fill="#7a2e00" stroke="#7a2e00" strokeWidth="1.5"/>
                <circle cx={ppx} cy={ppy} r={5} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.2"/>
              </>;
            })()}
            <text x={cx} y={cy+Ro+22} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Internal-Tooth Ratchet</text>
            <text x={cx} y={cy+Ro+35} textAnchor="middle" fontSize="8.5" fill="#a04010">Compact; pawl on inner hub; ring is housing</text>
          </g>;
        })()}

        {/* ─── 4. Silent / roller freewheel ─── */}
        {(()=>{
          const cx=848, cy=148, Ro=78, Ri=50, rollers=10;
          // Cam ring (slightly eccentric lobes)
          return <g>
            <circle cx={cx} cy={cy} r={Ro} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
            <circle cx={cx} cy={cy} r={Ri} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.8"/>
            <circle cx={cx} cy={cy} r={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Rollers + cam pockets */}
            {Array.from({length:rollers},(_,i)=>{
              const a=(2*Math.PI*i)/rollers - Math.PI/2;
              const rr=5;
              // Cam wedge: roller centre at slightly variable radius
              const rDist=Ri+10 + (i%2===0?2:-1);
              const rcx=cx+rDist*Math.cos(a), rcy=cy+rDist*Math.sin(a);
              return <g key={i}>
                {/* Cam pocket wedge */}
                <path d={`M ${cx+(Ri-2)*Math.cos(a-0.28)} ${cy+(Ri-2)*Math.sin(a-0.28)}
                  L ${cx+(Ro-2)*Math.cos(a-0.18)} ${cy+(Ro-2)*Math.sin(a-0.18)}
                  L ${cx+(Ro-2)*Math.cos(a+0.08)} ${cy+(Ro-2)*Math.sin(a+0.08)}
                  L ${cx+(Ri-2)*Math.cos(a+0.14)} ${cy+(Ri-2)*Math.sin(a+0.14)} Z`}
                  fill="#d4874a" stroke="#7a2e00" strokeWidth="1" opacity="0.7"/>
                <circle cx={rcx} cy={rcy} r={rr} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.2"/>
              </g>;
            })}
            <text x={cx} y={cy+Ro+22} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Roller / Sprag Freewheel</text>
            <text x={cx} y={cy+Ro+35} textAnchor="middle" fontSize="8.5" fill="#a04010">Silent; no pawl click; high speed capable</text>
          </g>;
        })()}

        <defs>
          <marker id="arVR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>
      </svg>
      <div style={S.dCaption}>All four variants share the same one-way principle — they differ in geometry (rotary vs. linear), tooth location (external vs. internal), and engagement mechanism (positive pawl vs. friction-wedge roller)</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Ratchet as an indexing / intermittent feed mechanism
// 4 steps, each step has its own column. Wider spacing, taller SVG.
// ─────────────────────────────────────────────────────────────────────────────
function DiagramIndexing() {
  const W = 920, H = 320;
  // 4 columns with generous spacing — leave room for holding pawl on left of each wheel
  const colCx = [118, 338, 558, 778], cy = 142;
  const R = 68, teeth = 8, td = 10;
  const crankAngles = [0, Math.PI/2, Math.PI, 3*Math.PI/2];
  const crankR = 48;

  // Step labels — explicit strings, no concatenation
  const stepLabels = [
    { title: 'Step 1 / 0°',   sub: 'Crank at top · engage' },
    { title: 'Step 2 / 90°',  sub: 'Drive stroke · +1 tooth' },
    { title: 'Step 3 / 180°', sub: 'Crank at bottom · +1 tooth' },
    { title: 'Step 4 / 270°', sub: 'Retract stroke' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Ratchet as an Indexing Mechanism — 4-Step Sequence (8-tooth wheel, 1 tooth per stroke)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arIX" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {crankAngles.map((ca, si) => {
          const x = colCx[si];
          const wheelPhase = (si * 2 * Math.PI) / teeth;
          const crankX = x + crankR * Math.cos(ca - Math.PI/2);
          const crankY = cy + crankR * Math.sin(ca - Math.PI/2);
          const engageToothI = si % teeth;
          const isDownstroke = ca <= Math.PI;

          return (
            <g key={si}>
              {/* Wheel */}
              <circle cx={x} cy={cy} r={R+3} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
              <circle cx={x} cy={cy} r={R-td-8} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
              <circle cx={x} cy={cy} r={9} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
              <path d={ratchetTeethPath(x, cy, R, teeth, td)}
                fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>

              {/* Reference mark */}
              <line x1={x} y1={cy}
                x2={x + (R-td-2)*Math.cos(-Math.PI/2 + wheelPhase)}
                y2={cy + (R-td-2)*Math.sin(-Math.PI/2 + wheelPhase)}
                stroke="#1a0e06" strokeWidth="2.5"/>
              <circle
                cx={x + (R-td-2)*Math.cos(-Math.PI/2+wheelPhase)}
                cy={cy + (R-td-2)*Math.sin(-Math.PI/2+wheelPhase)}
                r={4} fill="#1a0e06"/>

              {/* Holding pawl — fixed to left of wheel */}
              {(()=>{
                const hta = -Math.PI/2 - (2*Math.PI*0.6)/teeth + wheelPhase;
                const htx = x+(R-1)*Math.cos(hta), hty = cy+(R-1)*Math.sin(hta);
                const hpx = x - R - 24, hpy = cy - 16;
                const hpa = Math.atan2(hty-hpy, htx-hpx), hpw=7;
                const hpl = Math.sqrt((htx-hpx)**2+(hty-hpy)**2);
                const hc=(al,pr)=>[hpx+al*Math.cos(hpa)-pr*Math.sin(hpa),hpy+al*Math.sin(hpa)+pr*Math.cos(hpa)];
                const [a,b,d,e]=[hc(0,-hpw/2),hc(0,hpw/2),hc(hpl+4,hpw/2),hc(hpl+4,-hpw/2)];
                return <>
                  <polygon points={`${a[0]},${a[1]} ${b[0]},${b[1]} ${d[0]},${d[1]} ${e[0]},${e[1]}`}
                    fill="#7a2e00" stroke="#7a2e00" strokeWidth="1.2" opacity="0.85"/>
                  <circle cx={hpx} cy={hpy} r={4.5} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.2"/>
                </>;
              })()}

              {/* Crank arm */}
              <line x1={x} y1={cy} x2={crankX} y2={crankY}
                stroke="#7a2e00" strokeWidth="3"/>
              <circle cx={crankX} cy={crankY} r={6} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>

              {/* Driving pawl from crank end to wheel tooth */}
              {(()=>{
                const dta = (2*Math.PI*(engageToothI+(isDownstroke?0:0.5)))/teeth - Math.PI/2 + wheelPhase;
                const dtx = x+(R-1)*Math.cos(dta), dty = cy+(R-1)*Math.sin(dta);
                const dpa = Math.atan2(dty-crankY, dtx-crankX), dpw=7;
                const dpl = Math.sqrt((dtx-crankX)**2+(dty-crankY)**2);
                const dc=(al,pr)=>[crankX+al*Math.cos(dpa)-pr*Math.sin(dpa),crankY+al*Math.sin(dpa)+pr*Math.cos(dpa)];
                const [a,b,d,e]=[dc(0,-dpw/2),dc(0,dpw/2),dc(dpl,dpw/2),dc(dpl,-dpw/2)];
                return <polygon points={`${a[0]},${a[1]} ${b[0]},${b[1]} ${d[0]},${d[1]} ${e[0]},${e[1]}`}
                  fill="#c45a10" stroke="#7a2e00" strokeWidth="1.2" opacity="0.9"/>;
              })()}

              {/* Step label box */}
              <rect x={x-62} y={cy+R+14} width={124} height={38} rx={4}
                fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
              <text x={x} y={cy+R+28} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">
                {stepLabels[si].title}
              </text>
              <text x={x} y={cy+R+42} textAnchor="middle" fontSize="8.5" fill="#a04010">
                {stepLabels[si].sub}
              </text>

              {/* Arrow to next step */}
              {si < 3 && (
                <line
                  x1={x + R + 12} y1={cy}
                  x2={colCx[si+1] - R - 12} y2={cy}
                  stroke="#c45a10" strokeWidth="2" markerEnd="url(#arIX)"/>
              )}
            </g>
          );
        })}

        {/* Summary bar */}
        <rect x={10} y={H-28} width={W-20} height={20} rx={4} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={W/2} y={H-13} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">
          Black mark = reference · wheel indexes exactly 1 tooth (45°) per drive stroke · dark pawl (left) holds wheel during retract
        </text>
      </svg>
      <div style={S.dCaption}>Indexing accuracy = 360°/z per step — for precision feed, use a larger tooth count or a Geneva mechanism in series with the ratchet to achieve exact angular increments</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Applications overview
// ─────────────────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 900, H = 180;
  const apps = [
    { label:'Socket Wrench', sub:'Driving & holding pawl\nQuick direction switch', x:80 },
    { label:'Bicycle Freewheel', sub:'Roller/sprag type\nSilent engagement', x:230 },
    { label:'Hoist / Winch', sub:'Holding pawl\nDrop-prevention safety', x:380 },
    { label:'Tie-Down Strap', sub:'Linear rack ratchet\nHigh load, compact', x:530 },
    { label:'Watch Movement', sub:'Escapement ratchet\n<0.1° resolution', x:680 },
    { label:'Wheelchair Brake', sub:'Anti-rollback\nADA safety standard', x:830 },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Major Application Domains</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {apps.map((a,i)=>(
          <g key={i}>
            <rect x={a.x-64} y={20} width={128} height={110} rx={6}
              fill={i%2===0?'#fdf0e4':'#fdf4ec'} stroke="#c45a10" strokeWidth="1.8"/>
            {/* Mini ratchet icon */}
            {(()=>{
              const icx=a.x, icy=52, iR=22, iTeeth=8, iTd=5;
              return <>
                <path d={ratchetTeethPath(icx,icy,iR,iTeeth,iTd)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.2" opacity="0.8"/>
                <circle cx={icx} cy={icy} r={iR-iTd-5} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.2"/>
                <circle cx={icx} cy={icy} r={5} fill="#7a2e00"/>
              </>;
            })()}
            <text x={a.x} y={90} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">{a.label}</text>
            {a.sub.split('\n').map((line,j)=>(
              <text key={j} x={a.x} y={104+j*13} textAnchor="middle" fontSize="8" fill="#a04010">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Ratchets span twelve orders of magnitude in size and torque — from watch escapements (microgram·mm) to mooring winches (megagram·metre)</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function RatchetPawlPage() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Ratchet &amp; Pawl Systems — Mechanical Engineering Reference</h1>

      <p style={S.p}>
        A <strong>ratchet and pawl</strong> is among the oldest and most fundamental mechanisms in mechanical engineering:
        a toothed wheel (the <em>ratchet</em>) paired with a spring-loaded catch (the <em>pawl</em>) that permits rotation
        in one direction only, blocking the reverse with positive, near-instantaneous mechanical lock. The principle
        appears at every scale from micro-watch escapements to multi-tonne marine mooring winches, and in both rotary
        and linear forms.
      </p>

      <h2 style={S.h2}>Anatomy &amp; Operating Principle</h2>
      <p style={S.p}>
        The mechanism has five essential parts: the <strong>ratchet wheel</strong> with asymmetric teeth, a
        <strong> driving or holding pawl</strong>, a <strong>pivot pin</strong> for the pawl, a <strong>return spring</strong>
        to maintain tooth engagement, and a <strong>frame</strong> that reacts loads. The critical design element is
        the tooth asymmetry — a near-radial driving face provides a self-locking condition, while a sloped back face
        allows the pawl to ride over teeth during the permitted direction.
      </p>
      <DiagramAnatomy />
      <Table2 rows={[
        ['Ratchet wheel','Toothed disc or rack with asymmetric teeth: steep driving face (5–15° from radial) + sloped back face (20–45°)\nMaterial: medium carbon steel (C45, 4140) case-hardened to 58–62 HRC on tooth flanks\nTooth count z: 6–72 typical; finer teeth → smaller dead angle → better resolution'],
        ['Pawl','Spring-loaded lever that engages tooth driving face under reverse load\nPivoted: tip describes arc — geometry must ensure tip enters tooth root correctly at all positions\nTip hardened to 60–64 HRC; tip radius 0.5–2 mm to match root fillet and limit contact stress'],
        ['Return spring','Maintains pawl tip contact at all times during forward stroke\nLeaf spring or coil torsion spring; pre-load force = 1.2–2× calculated back-face lift force\nOver-stiff spring increases drag loss on forward stroke; under-stiff spring causes missed engagement'],
        ['Pivot pin','Transfers full locking torque from pawl to frame as radial bearing load on pin\nF_pin = T_lock / L_pawl  (T_lock = applied torque, L_pawl = moment arm from pivot to tooth contact)\nSized for shear and bearing; typically case-hardened alloy steel press-fit into frame'],
        ['Frame / housing','Reacts all loads: locking force on pawl pivot + any axial load on ratchet shaft\nRigidity critical — frame deflection can cause pawl tip to miss tooth root under high load\nOften cast iron, steel fabrication, or aluminium alloy depending on weight and load class'],
      ]}/>

      <h2 style={S.h2}>Operating Configurations</h2>
      <p style={S.p}>
        The same wheel and pawl can be arranged in four fundamentally different configurations, each producing a
        different functional result. Understanding these is essential for correct mechanism selection in design.
      </p>
      <DiagramModes />
      <Table2 rows={[
        ['Holding pawl','Pawl fixed to frame; wheel free to rotate forward (e.g. by hand crank or motor)\nOn release of driving force, wheel immediately locked by holding pawl\nApplications: winch, hoist, jack screw, hand-operated pump, safety brake'],
        ['Driving pawl','Pawl mounted on oscillating lever or eccentric; drives wheel forward on down-stroke\nBack-stroke is free — driving pawl rides over teeth; holding pawl (if separate) prevents rollback\nApplications: ratchet wrench, manual winch with lever, pump jack, step-feed mechanism'],
        ['Double pawl (anti-backlash)','Two pawls offset by half tooth pitch (180°/z)\nReduces dead angle from 360°/z to 180°/z — one pawl always in engagement within half pitch\nApplications: high-precision indexing, safety-critical hoists (ASME HST-1 requires ≤15° dead angle)'],
        ['Reversible ratchet','Pawl pivot or body designed to flip/rotate 180° to engage opposite tooth face\nWheel must be symmetric or dual-profiled; often a selector plate changes engagement side\nApplications: socket wrenches (most common), reversible hand-operated feed mechanisms'],
      ]}/>

      <h2 style={S.h2}>Tooth Geometry &amp; Self-Locking Analysis</h2>
      <p style={S.p}>
        The self-locking condition is the most important theoretical aspect of ratchet design. If the driving face
        angle α is less than the friction angle φ = arctan(μ), no amount of reverse torque can eject the pawl —
        locking is geometrically guaranteed regardless of load magnitude. This is the fundamental difference between
        a ratchet and a friction-only device such as a wrap-spring clutch.
      </p>
      <DiagramToothGeometry />
      <Table2 rows={[
        ['Tooth pitch formula','p = π · D / z  where D = pitch circle diameter (mm), z = number of teeth\np also equals the arc length the wheel advances per index step when used as a counter or feed'],
        ['Driving face angle α','5° – 15° from radial (measured at tip)\nSmaller α → stronger locking; larger α → slightly easier pawl entry under load\nα = 0° (truly radial face) is ideal for locking but can bind; 8–12° is the common industry compromise'],
        ['Back face angle β','20° – 45° from radial\nLarger β → lower spring force needed to ride over tooth, lower wear; but tooth becomes weaker\nOptimum β typically 25–35° balancing cam-over force and tooth root bending strength'],
        ['Self-locking condition','α < φ  (φ = friction angle = arctan μ)\nFor μ = 0.15 (lubricated steel):  φ ≈ 8.5° — so α ≤ 8° gives positive lock with margin\nFor μ = 0.10 (very well lubricated):  φ ≈ 5.7° — use α ≤ 5° for safety margin'],
        ['Normal force on pawl tip','F_N = T_load / (z · R · sin(α + φ))  (approximate, single tooth)\nActual distribution: depending on stiffness, 1–3 teeth may share load\nConservative design assumes single-tooth contact'],
        ['Pawl pivot force','F_pivot = F_N · (L_tip / L_pivot)  (moment arm ratio)\nLarger pivot-to-tip distance → lower pivot bearing load → preferred for high-torque designs\nTypical arm ratio: L_tip / L_pivot = 2–5'],
        ['Back-face lift force','F_lift = F_N_back · tan(β)  (force spring must overcome to allow tooth crossing)\nF_N_back generated by output torque on back face during forward rotation = T_drag / R\nDrag torque T_drag should be near zero in a driven (non-braking) ratchet'],
        ['Hertz contact stress at tip','σ_H ≈ 0.418 √(2 F_N E* / (r_tip L))  (cylindrical contact)\nr_tip = pawl tip radius (mm), L = axial face width (mm), E* = reduced modulus\nTarget: σ_H < 1000–1200 MPa for hardened steel contact pairs (infinite life range)'],
      ]}/>

      <h2 style={S.h2}>Dead Angle &amp; Resolution</h2>
      <p style={S.p}>
        The <strong>dead angle</strong> (also called free-play or back-lash angle) is the maximum angular displacement
        of the ratchet wheel in the reverse direction before the pawl re-engages. For a single-tooth pawl this equals
        one full tooth pitch angle. It directly limits the useful resolution of any indexing, measurement, or
        safety-critical application.
      </p>
      <DiagramDeadAngle />
      <Table2 rows={[
        ['Dead angle — single pawl','θ_dead = 360° / z  (one full tooth pitch)\nExample: z=12 → 30° dead angle; z=36 → 10° dead angle\nThis is the maximum reverse travel before re-engagement — actual may be less if pawl engages mid-pitch'],
        ['Dead angle — dual pawl','θ_dead = 180° / z  (half pitch — one pawl always within half-tooth of engagement)\nExample: z=12 dual → 15°; z=24 dual → 7.5°\nRequires two independent springs and pivots; adds complexity but critical for safety hoists'],
        ['Increasing resolution','More teeth z → smaller dead angle → better resolution (also smaller step in indexing use)\nPractical upper limit: z ≈ 60–72 due to tooth strength (small pitch → thin tooth root)\nAlternative: multi-stage ratchet (large wheel + fine vernier) for very high resolution needs'],
        ['Noise and click rate','Audible click per tooth on forward stroke: click frequency = z × n (n = rotational speed, rev/s)\nBicycle freewheel: z=36 typical → 36 clicks/rev; loud = feedback advantage for tools\nSilent alternatives (roller/sprag freewheel) used when noise is undesirable (instruments, medical)'],
      ]}/>

      <h2 style={S.h2}>Ratchet Mechanism Variants</h2>
      <p style={S.p}>
        The ratchet principle extends well beyond the classic external toothed wheel. Internal-tooth ratchets, linear
        rack ratchets, and rolling-element freewheels all achieve the same one-way transmission function with different
        geometry, space envelopes, and performance characteristics.
      </p>
      <DiagramVariants />
      <Table2 rows={[
        ['External-tooth rotary','Most common form; teeth on outside of disc; pawl pivots on external frame\nSimple to manufacture (hobbing or shaping); direct access to teeth for inspection\nUsed in: wrenches, winches, hand tools, meters, conveyor backstops'],
        ['Internal-tooth rotary','Teeth on inside of a ring; pawl pivots on central hub/shaft\nMore compact radial envelope; housing doubles as ring gear for integration with other mechanisms\nUsed in: bicycle hubs (alongside roller ratchet), some winch drums, planetary backstops'],
        ['Linear rack ratchet','Teeth on a flat or curved bar; pawl fixed to frame engages rack teeth\nTranslational equivalent: permits linear motion in one direction only\nUsed in: ratchet tie-downs (cargo straps), floor jacks, rack-and-pawl jacking systems, hand clamps'],
        ['Roller/sprag freewheel','Rollers or sprags wedge between inner and outer race via cam geometry — no discrete teeth\nSilent engagement (no click); higher speed capability; lower dead angle (continuous lock)\nUsed in: bicycle cassettes and bottom brackets, automotive starter one-way clutch, conveyor backstops\nLower shock capacity than toothed ratchet; requires clean lubrication; not field-repairable'],
        ['Detent ratchet (ball/spring)','Ball pressed into sockets by spring; low torque, 360°-symmetric engagement option\nUsed as: position detents in multi-position switches, adjustable arm joints, camera mounts\nNot a true one-way lock — may slip if torque > detent force; supplementary lock needed for safety'],
        ['Escapement ratchet (watch/clock)','Precision micro-scale ratchet; advance controlled tooth-by-tooth by oscillating pallet fork\nDeadbeat escapement: pallet holds gear absolutely still between impulses\nUsed in: mechanical watches and clocks; also industrial step-counters and mechanical timers'],
      ]}/>

      <h2 style={S.h2}>Ratchet as an Indexing &amp; Intermittent-Feed Mechanism</h2>
      <p style={S.p}>
        When a ratchet wheel is driven by an oscillating lever or crank, it converts continuous rotation or reciprocation
        into discrete, equal angular steps. This is the basis of <strong>intermittent-feed mechanisms</strong> used in
        textile machines, printing presses, screw-machine cross-slides, and film projectors.
      </p>
      <DiagramIndexing />
      <Table2 rows={[
        ['Index angle per stroke','Δθ = 360° / z  per drive stroke (one tooth advance)\nFor variable step: adjustable driving pawl arm length changes effective engagement teeth per stroke\nFor sub-pitch resolution: vernier dual-wheel ratchet with different tooth counts on two co-axial wheels'],
        ['Step accuracy','Governed by tooth pitch uniformity and pawl tip wear\nAccuracy class: industrial ±0.1°/step (z=48); precision ±0.02°/step (z=120 hobbed)\nHolding pawl must fully seat before load is applied — minimum dwell angle required in cam design'],
        ['Feed calculation','Linear feed per stroke = p × (teeth advanced per stroke)\nFor screw-machine cross-slide: feed = p_thread × (ratchet turns per stroke)\nAdjustment range: 1 tooth minimum to z/4 teeth maximum per stroke (practical limits)'],
        ['Oscillation source','Crank and connecting rod: constant stroke amplitude → constant index per cycle\nCam and follower: programmable dwell allows indexing only during desired phase of machine cycle\nPneumatic/hydraulic actuator: adjustable stroke → variable index; common in modern automated machinery'],
      ]}/>

      <h2 style={S.h2}>Force &amp; Torque Analysis</h2>
      <Table2 rows={[
        ['Locking torque capacity','T_lock = F_N_max × R × cos(α)  (tangential component of max allowable normal force)\nF_N_max limited by Hertz contact stress at pawl tip and bending stress at tooth root\nVerify both: contact fatigue and root bending are independent failure modes'],
        ['Tooth root bending','M_root = F_N × h_tooth  (cantilever moment at tooth root)\nσ_b = M_root × c / I  (c = distance to extreme fibre, I = root cross-section second moment)\nTarget σ_b < 0.4 × yield strength of wheel material for infinite life (conservative)'],
        ['Pawl pivot bearing load','F_pivot = F_N × L_arm / L_pivot  where L_arm = pivot-to-contact length, L_pivot = separate if not at base\nFor inline pawl (pivot behind tip in line of force): F_pivot ≈ F_N — simple compression\nFor offset geometry: resolve into radial + tangential components separately'],
        ['Spring pre-load sizing','F_spring_min = F_lift_max = T_drag / R × tan(β)\nF_spring_recommended = 2 × F_spring_min  (safety factor for wear and assembly variation)\nSpring rate k: chosen so full pawl lift (≈ tooth depth) occurs at 3 × F_spring_min force'],
        ['Inertia considerations','High-speed ratchets: pawl inertia may prevent full seating within available dwell time\nCheck: t_engage = √(2θ_engage / α_pawl)  where α_pawl = spring torque / pawl moment of inertia\nIf t_engage > available dwell time → increase spring force, reduce pawl mass, or use finer pitch'],
      ]}/>

      <h2 style={S.h2}>Materials &amp; Manufacturing</h2>
      <Table2 rows={[
        ['Ratchet wheel steels','Light duty: C45 (SAE 1045) normalized → 200 HB; inexpensive, easily machined\nMedium duty: 4140 (42CrMo4) Q&T to 280–320 HB, surface-hardened flanks to 56–60 HRC\nHeavy duty / high-cycle: 4320 (20CrNiMo) carburized + quenched → 60–64 HRC case, tough core\nStainless: 17-4 PH H900 for corrosive environments (marine, food, medical)'],
        ['Pawl materials','Same as wheel but often one grade higher hardness to prevent tip blunting\nHigh-cycle tools (socket wrench): powder-metal or bar stock 8620 carburized, 62 HRC tip\nCast iron pawls: acceptable for very low duty (< 10,000 cycles) furniture/architectural hardware\nPolymer pawls (Delrin/nylon): light loads only, silent operation, corrosion immunity, low tooling cost'],
        ['Manufacturing — wheel','Gear hobbing: most accurate, ±0.01 mm pitch; suitable for DIN 6–8 quality ratchet wheels\nBroaching: fast for internal-tooth rings and racks; excellent surface finish\nPunching/blanking: low-cost for thin wheels (< 8 mm); burrs must be removed from tooth tips\nSintered PM: near-net-shape, good for small complex geometry, ±0.05 mm tolerance class'],
        ['Manufacturing — pawl','CNC turning + milling: normal approach for medium-to-high tolerance pawls\nForging + finish grind: highest strength, used in safety-critical hoisting pawls\nWire EDM: precision small pawls (watch, instrument) to ±0.005 mm\nTip geometry verification: optical CMM or shadow-graph on sample basis'],
        ['Surface treatments','Case hardening: adds 0.5–2.0 mm hard case; maintains tough core for shock resistance\nThin dense chrome (TDC): 25–75 μm, hardness 900–1100 HV, low friction, corrosion resistance\nPVD TiN/TiCN: 2–5 μm, 2000–3000 HV, used on precision watch pawls and aerospace ratchets\nPhosphating + oil: low cost corrosion protection for general industry; reduces galling on running-in'],
      ]}/>

      <h2 style={S.h2}>Failure Modes &amp; Maintenance</h2>
      <Table2 rows={[
        ['Pawl tip wear (most common)','High Hertz contact → progressive tip wear → increased dead angle → eventual missed engagement\nSymptom: freeplay increases; audible "skip" under load before full lock\nPrevention: correct tip radius, hardness, lubrication; replacement interval based on dead-angle measurement'],
        ['Tooth root fatigue','Cyclic bending load at tooth root → crack initiating at root fillet\nCritical in high-cycle indexing (> 10^6 cycles); less common in infrequent-use safety devices\nPrevention: generous root radius (≥ 0.3 × module), surface hardening, shot-peening root fillet'],
        ['Spring fatigue / breakage','Return spring under cyclic flexure → fatigue at max-stress point (usually bend radius)\nSymptom: intermittent engagement failure; pawl fails to seat fully before load applied\nInspection: check spring free-length and pre-load force at each major service interval'],
        ['Corrosion','Pit initiation on tooth flank → stress concentration → tooth fracture under shock load\nParticularly critical in marine (chloride) and food/pharmaceutical (cleaning chemical) environments\nPrevention: material selection (stainless, coated), sealed housing, regular re-lubrication'],
        ['Overload / shock fracture','Single event overload → tooth fracture or pawl shear through pivot\nSymptom: immediate loss of locking function; often catastrophic in lifting applications\nPrevention: torque limiter or shear pin in series; proof-load testing of safety-critical assemblies'],
        ['Inspection schedule','Before each use (safety-critical hoist/winch): visual check, confirm audible click engagement\nPeriodic (every 500–2000 operating hours): measure dead angle, check spring force, inspect tips\nOverhaul: disassemble, measure tooth profile and tip radius, replace spring, re-lubricate\nHoisting ratchets (ASME B30.21): mandatory annual inspection and proof-load test'],
      ]}/>

      <h2 style={S.h2}>Applications</h2>
      <DiagramApplications />
      <Table2 rows={[
        ['Hand tools','Socket wrenches: driving + holding pawl, reversible selector, z=32–60, hardened steel, 1000+ N·m rated\nRatchet screwdrivers: fine-pitch (z=72+) for low dead angle; click torque wrenches use calibrated spring pawl\nPliers/clamps: ratcheting locking pliers; rack ratchet in trigger-action pliers'],
        ['Lifting & hoisting','Chain hoists (block and tackle): holding pawl is primary safety device — must hold 1.5× rated load\nWinches (manual and electric): holding pawl prevents drum reverse on power loss\nASME HST-1 / EN 13157 require dead angle ≤ 15° and pawl proof-load ≥ 2× SWL\nSelf-locking worm gear often paired with ratchet for redundant safety in personnel-rated hoists'],
        ['Cargo & load securing','Ratchet tie-down straps (EN 12195-2): linear rack ratchet, 2500–5000 kg rated tension\nCargo winches on flatbed trucks: rotary ratchet, steel wire or synthetic rope\nTurnbuckles with ratchet: structural load applications where threaded adjustment is impractical'],
        ['Cycling','Cassette freehub: spline-type ratchet ring or star-ratchet (DT Swiss); z=18–72 teeth\nPedal mechanism: older 3-pawl design (36-48 tooth ring); modern star ratchet: 18–36 points of engagement\nTorque capacity: track (fixed) → n/a; MTB rear hub → 300–900 N·m impact rated'],
        ['Automotive & aerospace','Starter motor one-way clutch: roller or sprag freewheel; 10,000+ RPM engagement speed\nSeatbelt retractor: ratchet pawl on spool — inertia lock on crash deceleration\nHelicopter freewheel unit: sprag clutch allows autorotation; safety-critical, FAA-certified design\nActuator backstops: linear actuator ball-screw ratchet prevents reversal on hydraulic failure'],
        ['Precision & instrumentation','Mechanical watches: escapement ratchet (crown wheel + pallets), z=15–20, 3 Hz typical\nOptical instrument fine-focus: z=60–120, 3° dead angle, phosphor bronze pawl on brass wheel\nMicrometer ratchet stop: slip-clutch + ratchet to apply consistent 5–8 cN·m measuring force\nDNA sequencing machines, PCR carousel: stepper motor + ratchet for index step verification'],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Tooth pitch:  p = π · D / z   (mm;  D = pitch diameter, z = tooth count)",
        "Dead angle (single pawl):  θ_dead = 360° / z   → finer teeth = better resolution",
        "Dead angle (dual pawl):  θ_dead = 180° / z   → half-pitch offset gives 2× resolution",
        "Self-locking condition:  α < φ   where φ = arctan(μ)   (α = driving face angle from radial)",
        "Friction angle (steel, lubricated):  φ ≈ 5.7° – 11.3°   (μ = 0.10 – 0.20)",
        "Normal force on pawl tip:  F_N = T_lock / (R · cos α)   (single-tooth contact, R = wheel radius)",
        "Pawl pivot force:  F_pivot = F_N · (L_tip / L_pivot)   (moment arm ratio)",
        "Back-face cam lift force:  F_lift = F_N_back · tan(β)   (β = back face angle)",
        "Spring pre-load (minimum):  F_spring ≥ 2 · F_lift   (factor of 2 for wear and assembly variation)",
        "Hertz contact stress (cylindrical):  σ_H ≈ 0.418 √(2 F_N E* / (r_tip · L))",
        "Tooth root bending stress:  σ_b = F_N · h_tooth / W_t   (W_t = section modulus at root)",
        "Index angle per step:  Δθ = 360° / z   (per tooth advanced by driving pawl per stroke)",
        "Click frequency (forward rotation):  f_click = z · n   (Hz;  n = rotational speed in rev/s)",
      ]}/>
    </div>
  );
}